import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ModalWrapper } from "@msuite/ui/modal";
import { useDataTableContext } from "@msuite/ui/table";
import type { Enums } from "@msuite/utils/types";
import { Fragment } from "react";
import { useCitadel } from "@/hooks/use-citadel";
import { AddMailboxModal } from "@/modals/mailboxes/add-mailbox-modal";

/** Props Interface */
interface MailboxesMenuProps {
	purpose: Enums<"mailboxPurpose">;
}

export function MailboxesMenu({ purpose }: MailboxesMenuProps) {
	/** Context */
	const { refetch } = useDataTableContext();

	/** Hooks */
	const { t } = useTranslation();
	const { citadel } = useCitadel();
	const addMailboxModal = useDisclosure();

	/** Computed */
	// `POST /mailboxes` is gated on `create Integration`, while the tab only needs
	// `read Integration` — so a caller who may see the connected mailboxes is not thereby allowed
	// to connect one. A rule list still in flight is not a denial.
	const createIsDenied = !!citadel && citadel.cannot("create", "Integration");

	/** Render */
	return (
		<Fragment>
			<Button
				icon={<Icon name="Plus" />}
				variant="primary"
				onClick={addMailboxModal.onOpen}
				isDisabled={createIsDenied}
				data-cy="add-mailbox-button"
			>
				{t("mailboxes.add_mailbox")}
			</Button>
			<ModalWrapper>
				{addMailboxModal.isOpen && (
					<AddMailboxModal
						isOpen={addMailboxModal.isOpen}
						onClose={addMailboxModal.onClose}
						purpose={purpose}
						onComplete={refetch}
					/>
				)}
			</ModalWrapper>
		</Fragment>
	);
}
